import { useCallback, useContext, useEffect, useRef, useState } from "react";

import {
   getNotifications,
   getUnreadNotificationsCount,
} from "../api/notificationsService";
import {
   getCurrentPopup,
   subscribePopup,
} from "../components/Common/popupUIStore";
import { isPushEnabled, showPushNotification } from "../utils/pushNotifications";
import {
   notificationBody,
   notificationId,
   notificationTitle,
} from "../utils/notificationsFormat";
import { UserContext } from "../UserContext";

const POLL_MS = 60_000;

export default function useNotificationsUnread() {
   const { user, loading } = useContext(UserContext);
   const canAccess = Boolean(user?.id);

   const [unreadCount, setUnreadCount] = useState(0);

   const prevCountRef = useRef(0);
   const seenPushIdsRef = useRef(new Set());
   const initializedRef = useRef(false);

   // Afișează push nativ pentru notificările necitite nou apărute.
   const maybePush = useCallback(async () => {
      if (!isPushEnabled()) return;
      try {
         const { items } = await getNotifications({
            page: 1,
            limit: 5,
            unreadOnly: true,
         });
         for (const item of items) {
            const id = String(notificationId(item) ?? "");
            if (!id || seenPushIdsRef.current.has(id)) continue;
            seenPushIdsRef.current.add(id);
            showPushNotification({
               title: notificationTitle(item),
               body: notificationBody(item),
               tag: `notification-${id}`,
               data: { id },
            });
         }
      } catch {
         // silent
      }
   }, []);

   const refresh = useCallback(async () => {
      if (!canAccess) {
         setUnreadCount(0);
         return 0;
      }

      try {
         const count = await getUnreadNotificationsCount();
         const prev = prevCountRef.current;
         prevCountRef.current = count;
         setUnreadCount(count);

         // La prima încărcare doar memorăm id-urile deja existente, fără push.
         if (!initializedRef.current) {
            initializedRef.current = true;
            try {
               const { items } = await getNotifications({
                  page: 1,
                  limit: 20,
                  unreadOnly: true,
               });
               items.forEach((item) => {
                  const id = String(notificationId(item) ?? "");
                  if (id) seenPushIdsRef.current.add(id);
               });
            } catch {}
         } else if (count > prev) {
            maybePush();
         }

         return count;
      } catch {
         setUnreadCount(0);
         return 0;
      }
   }, [canAccess, maybePush]);

   useEffect(() => {
      if (loading || !canAccess) return undefined;
      refresh();

      const timer = setInterval(refresh, POLL_MS);
      return () => clearInterval(timer);
   }, [canAccess, loading, refresh]);

   useEffect(() => {
      if (!canAccess) return undefined;

      const handleFocus = () => refresh();
      const handleVisibility = () => {
         if (document.visibilityState === "visible") refresh();
      };

      window.addEventListener("focus", handleFocus);
      document.addEventListener("visibilitychange", handleVisibility);

      return () => {
         window.removeEventListener("focus", handleFocus);
         document.removeEventListener("visibilitychange", handleVisibility);
      };
   }, [canAccess, refresh]);

   // Reîmprospătează când se închide popup-ul de notificări (după marcarea citită).
   useEffect(() => {
      if (!canAccess) return undefined;

      let wasOpen = getCurrentPopup()?.type === "notifications";
      return subscribePopup(() => {
         const isOpen = getCurrentPopup()?.type === "notifications";
         if (wasOpen && !isOpen) refresh();
         wasOpen = isOpen;
      });
   }, [canAccess, refresh]);

   return { canAccess, unreadCount, refresh, setUnreadCount };
}
